import React, { useState } from 'react';
import { ModalV2, Tooltip } from 'components/common/modals';
import { IconButtonV2 } from 'components/common/ui';
import AddIcon from '@mui/icons-material/Add';
import { servicePointsServices } from '@services';
import { showError } from '@utils';

import { useServicePoints, useToggleModal } from '@hooks';
import { PermissionsGate } from 'components/common';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import AddServicePointForm from './AddServicePointForm';

const getPayload = (formValues) => {
  try {
    const payload = {
      internalId: `SP${formValues?.internalId}`,
      name: formValues?.spName,
      addresses:
        !!formValues?.address && !!Object.keys(formValues?.address).length
          ? [formValues?.address]
          : null,
      contacts: [
        {
          fullName: formValues?.contactFullName,
          role: formValues?.contactRole,
          phoneNumber: [
            {
              phoneNumber: formValues?.telephone,
              type: 'mobile',
              isDefault: true,
            },
          ],
          email: formValues?.email,
          isPrimary: true,
        },
      ],
      supportedManufacturers:
        !!formValues?.brands && !!formValues?.brands.length
          ? formValues?.brands
              ?.map((brand) => brand.value)
              ?.sort((a, b) => a - b)
          : [],
      supportedWorkTypes:
        !!formValues?.workTypes && !!formValues?.workTypes?.length
          ? formValues?.workTypes
              ?.map((workType) => workType.value)
              ?.sort((a, b) => a - b)
          : [],
      supportedFuelTypes:
        !!formValues?.fuelTypes && !!formValues?.fuelTypes?.length
          ? formValues?.fuelTypes
              ?.map((fuelType) => fuelType.value)
              ?.sort((a, b) => a - b)
          : [],
      supportedInsurances:
        !!formValues?.insurances && !!formValues?.insurances?.length
          ? formValues?.insurances
              ?.map((insurance) => insurance.value)
              ?.sort((a, b) => a - b)
          : [],
      supportedProviders:
        !!formValues?.affiliations && !!formValues?.affiliations?.length
          ? formValues?.affiliations
              ?.map((provider) => provider.value)
              ?.sort((a, b) => a - b)
          : [],
      billingData: {
        bank: formValues?.bank?.value || null,
        bankIban: formValues?.iban ? formValues?.iban?.replace(/\s/g, '') : '',
      },
    };

    return payload;
  } catch (error) {
    showError(error);
  }
};

const AddServicePointModal = ({ enums, onGetServicePoints }) => {
  const { handleClose, handleOpen, isOpen } = useToggleModal(false);
  const [isAdding, setIsAdding] = useState(false);
  const { invalidateServicePoints } = useServicePoints({ enabled: false });

  const handleSubmit = async (values) => {
    try {
      setIsAdding(true);
      const payload = getPayload(values);
      await servicePointsServices.addServicePoint(payload);
      invalidateServicePoints();
      onGetServicePoints();
      handleClose();
    } catch (error) {
      showError(error);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      <PermissionsGate entity={ENTITIES_PERMISIONS.WriteServicePoints}>
        <IconButtonV2 onClick={handleOpen} variant="text" size="small">
          <Tooltip title="Add Service Point" placement="top">
            <AddIcon data-testid="AddIcon" fontSize="small" />
          </Tooltip>
        </IconButtonV2>
      </PermissionsGate>
      <ModalV2
        id="add-service-point-modal"
        title="Add Service Point"
        isOpen={isOpen}
        isLoading={isAdding}
        onClose={handleClose}
      >
        <AddServicePointForm
          onSubmit={handleSubmit}
          onCancel={handleClose}
          isLoading={isAdding}
          enums={enums}
        />
      </ModalV2>
    </>
  );
};

export default AddServicePointModal;
